import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldLabel } from '@/components/FieldLabel'
import type { Layout } from '@/services/sites/layoutsApi'
import { withScheme } from '@/utils/url'
import {
  applyNamePattern,
  canHalveSize,
  layoutCategories,
  layoutCategoryKey,
  layoutFits,
  suggestLayout,
  type BulkCreativeDraft,
} from '@/services/creatives/bulkUpload'

export interface BulkEditToolbarProps {
  /** The drafts an edit applies to — the selection, or every draft when none is selected. */
  targets: BulkCreativeDraft[]
  /** Whether `targets` is a selection rather than the whole list. */
  selected: boolean
  layouts: Layout[]
  categoryLabel: (category: string) => string
  onChange: (patches: Record<string, Partial<BulkCreativeDraft>>) => void
  disabled?: boolean
}

/** Sets the name, category, layout or link of many drafts at once. */
export function BulkEditToolbar({
  targets,
  selected,
  layouts,
  categoryLabel,
  onChange,
  disabled,
}: BulkEditToolbarProps) {
  const { t } = useTranslation()
  const [namePattern, setNamePattern] = useState('')
  const [url, setUrl] = useState('')

  const ready = targets.filter((d) => d.status === 'ready')
  const usable = layouts.filter((l) => ready.some((d) => layoutFits(l, d)))
  const categories = layoutCategories(usable)
  const scalable = targets.filter(canHalveSize)
  const allHalved = scalable.length > 0 && scalable.every((d) => d.halfSize)

  const patchEach = (
    patch: (draft: BulkCreativeDraft, i: number) => Partial<BulkCreativeDraft> | null
  ) => {
    const patches: Record<string, Partial<BulkCreativeDraft>> = {}
    targets.forEach((draft, i) => {
      const p = patch(draft, i)
      if (p) patches[draft.id] = p
    })
    onChange(patches)
  }

  const applyCategory = (category: string) =>
    patchEach((draft) => {
      if (draft.status !== 'ready') return null
      const next = suggestLayout(layouts, { ...draft, category })
      // Leave a draft alone when nothing in the category can carry its file.
      if (!next || layoutCategoryKey(next) !== category) return null
      const current = layouts.find((l) => l.id === draft.layoutId)
      const keep = current && layoutCategoryKey(current) === category
      return { category, layoutId: keep ? current.id : next.id }
    })

  const applyLayout = (layoutId: string) => {
    const layout = layouts.find((l) => l.id === layoutId)
    if (!layout) return
    patchEach((draft) =>
      draft.status === 'ready' && layoutFits(layout, draft)
        ? { layoutId, category: layoutCategoryKey(layout) }
        : null
    )
  }

  const scope = selected
    ? t('creativeUpload.bulkScopeSelected', { count: targets.length })
    : t('creativeUpload.bulkScopeAll', { count: targets.length })

  return (
    <div className="flex flex-col gap-3 border-b bg-(--ad-primary-soft) px-(--card-spacing) py-3">
      <span className="text-sm font-medium text-primary">{scope}</span>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
        <div className="p-field">
          <FieldLabel tooltip={t('creativeUpload.namePatternHelp')}>{t('common.name')}</FieldLabel>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (!namePattern.trim()) return
              patchEach((draft, i) => ({ name: applyNamePattern(namePattern, draft, i + 1) }))
            }}
          >
            <Input
              value={namePattern}
              disabled={disabled}
              placeholder={t('creativeUpload.namePatternPlaceholder')}
              onChange={(e) => setNamePattern(e.target.value)}
            />
            <Button type="submit" variant="outline" disabled={disabled || !namePattern.trim()}>
              {t('common.apply')}
            </Button>
          </form>
        </div>

        <div className="p-field">
          <FieldLabel>{t('creativeUpload.category')}</FieldLabel>
          <Select
            label={t('creativeUpload.category')}
            value=""
            disabled={disabled || !categories.length}
            onValueChange={applyCategory}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('creativeUpload.setCategory')}>
                {t('creativeUpload.setCategory')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {categoryLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="p-field">
          <FieldLabel>{t('creatives.layout')}</FieldLabel>
          <Select
            label={t('creatives.layout')}
            value=""
            disabled={disabled || !usable.length}
            onValueChange={applyLayout}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('creativeUpload.setLayout')}>
                {t('creativeUpload.setLayout')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {usable.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name}
                  <span className="text-muted-foreground">
                    {' '}
                    · {categoryLabel(layoutCategoryKey(l))}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="p-field">
          <FieldLabel tooltip={t('creativeUpload.linkHelp')}>{t('creativeUpload.link')}</FieldLabel>
          {/* The browser's own URL check would reject a link typed without its scheme. */}
          <form
            className="flex gap-2"
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              const link = withScheme(url)
              setUrl(link)
              patchEach(() => ({ url: link }))
            }}
          >
            <Input
              type="url"
              value={url}
              disabled={disabled}
              placeholder="https://"
              onChange={(e) => setUrl(e.target.value)}
              onBlur={(e) => setUrl(withScheme(e.target.value))}
            />
            <Button type="submit" variant="outline" disabled={disabled}>
              {t('common.apply')}
            </Button>
          </form>
        </div>

        {scalable.length > 0 && (
          <div className="p-field">
            <FieldLabel tooltip={t('creativeUpload.halfSizeHelp')} htmlFor="bulk-half-size">
              {t('creativeUpload.halfSize')}
            </FieldLabel>
            {/* The same 2× toggle each row has, applied to every draft it can halve. */}
            <div className="flex h-9 items-center">
              <Button
                id="bulk-half-size"
                type="button"
                variant={allHalved ? 'default' : 'outline'}
                aria-pressed={allHalved}
                disabled={disabled}
                onClick={() =>
                  patchEach((draft) => (canHalveSize(draft) ? { halfSize: !allHalved } : null))
                }
              >
                2×
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
